/**
 * Phase 23 (Plan 23-02) — PendingForMeWidget.
 *
 * Three counts (Maker / Checker / Approver) — each row links to the
 * corresponding actor list page so the user can dispatch immediately.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import {
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetSkeleton,
} from '@/components/dashboard/widget-states';
import type { ApiError } from '@/lib/api/error';
import type { PendingForMeBlock } from '@/types/dashboard';

export interface PendingForMeWidgetProps {
  readonly data: PendingForMeBlock | null | undefined;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  readonly onRetry: () => void;
  readonly className?: string;
}

interface BodyProps {
  readonly data: PendingForMeBlock;
}

function PendingForMeBody({ data }: BodyProps): JSX.Element {
  return (
    <ul className="space-y-2" data-testid="pending-for-me-list">
      <li>
        <Link
          to="/logsheets/maker?status=Draft"
          className="hover:bg-accent/50 flex items-center justify-between rounded-md px-3 py-2 text-sm"
          data-testid="pending-for-me-maker"
        >
          <span>Maker — drafts &amp; rejected</span>
          <span className="font-semibold tabular-nums" data-testid="pending-for-me-maker-count">
            {String(data.makerCount)}
          </span>
        </Link>
      </li>
      <li>
        <Link
          to="/logsheets/checker/pending"
          className="hover:bg-accent/50 flex items-center justify-between rounded-md px-3 py-2 text-sm"
          data-testid="pending-for-me-checker"
        >
          <span>Checker — submitted queue</span>
          <span className="font-semibold tabular-nums" data-testid="pending-for-me-checker-count">
            {String(data.checkerCount)}
          </span>
        </Link>
      </li>
      <li>
        <Link
          to="/logsheets/approver/pending"
          className="hover:bg-accent/50 flex items-center justify-between rounded-md px-3 py-2 text-sm"
          data-testid="pending-for-me-approver"
        >
          <span>Approver — checked queue</span>
          <span className="font-semibold tabular-nums" data-testid="pending-for-me-approver-count">
            {String(data.approverCount)}
          </span>
        </Link>
      </li>
    </ul>
  );
}

export function PendingForMeWidget({
  data,
  isLoading,
  error,
  onRetry,
  className,
}: PendingForMeWidgetProps): JSX.Element {
  return (
    <WidgetCard
      title="Pending On Me"
      testId="pending-for-me-widget"
      {...(className !== undefined ? { className } : {})}
    >
      {isLoading ? (
        <WidgetSkeleton testId="pending-for-me-skeleton" rows={3} />
      ) : error ? (
        <WidgetError error={error} onRetry={onRetry} testId="pending-for-me-error" />
      ) : !data ||
        data.makerCount + data.checkerCount + data.approverCount === 0 ? (
        <WidgetEmpty
          message="Nothing pending right now."
          testId="pending-for-me-empty"
        />
      ) : (
        <PendingForMeBody data={data} />
      )}
    </WidgetCard>
  );
}
